import {notEmpty} from '@wandb/weave/common/util/obj';

import {captureError} from '../../integrations';
import {type Line, type Mark, type Point} from '../../util/plotHelpers/types';
import {type RenderLine} from './chartSeries';

// Font settings matching Figma design for crosshair tooltip
export const TOOLTIP_FONT_SIZE = 13;
export const TOOLTIP_FONT_FAMILY = "'Source Sans 3', sans-serif";
export const TOOLTIP_LINE_HEIGHT_RUN_NAMES = '17px'; // Source Sans 3
export const TOOLTIP_LINE_HEIGHT_VALUES = '18px'; // Inconsolata

// CSS styles for content in <strong> tags within tooltip legend rows.
// Uses Inconsolata SemiBold to distinguish from other text, which uses Source Sans 3.
export const VALUE_STYLES_CSS = `
  [data-test="line-plot-crosshair-content"] [data-row-key="legend-row"] strong {
    font-family: 'Inconsolata', monospace;
    font-weight: 600;
    line-height: ${TOOLTIP_LINE_HEIGHT_VALUES};
  }
`;

export type CrosshairPoints = {
  point: Point;
  minmaxPoint: Point | null;
  stddevPoint: Point | null;
  prevPoint: Point | null;
};

export function getAllCrosshairPointsFromLine(
  line: Pick<Line, 'data' | 'nanPoints'> & {
    minmaxLine?: Pick<Line, 'data'>;
    stddevLine?: Pick<Line, 'data'>;
  }
) {
  const nonNanCrosshairPoints: CrosshairPoints[] = line.data.map((p, i) => ({
    point: p,
    minmaxPoint: line.minmaxLine?.data[i] ?? null,
    stddevPoint: line.stddevLine?.data[i] ?? null,
    prevPoint: i > 0 ? line.data[i - 1] : null,
  }));
  const nanCrosshairPoints: CrosshairPoints[] =
    line.nanPoints?.map((p, i) => ({
      point: p,
      minmaxPoint: null,
      stddevPoint: null,
      prevPoint: i > 0 ? (line.nanPoints?.[i - 1] ?? null) : null,
    })) ?? [];
  const allCrosshairPoints = mergeCrosshairPoints(
    nonNanCrosshairPoints,
    nanCrosshairPoints
  );
  return allCrosshairPoints;
}

/**
 * Leftmost index i in `points` where points[i].point.x >= x. Replaces the lodash
 * `_.sortedIndexBy(points, key, ({point}) => point.x)` previously used here, which
 * invoked a closure on every binary-search probe and required a throwaway search-key
 * object. Reproduces sortedIndexBy's retHighest=false semantics, including NaN
 * x-values sorting high (NaN < x is false -> else branch). Assumes point.x is numeric,
 * which the surrounding code already requires (it does arithmetic on point.x).
 *
 * Intentionally NOT unified with `lowerBoundByY` (LinePlotCrosshair.tsx) into a generic
 * `lowerBound(items, target, getValue)`: a shared accessor would be called once per probe
 * on this hot path — the exact per-probe closure call this inline search exists to avoid
 * (vs lodash sortedIndexBy) — and isn't guaranteed to inline away. The duplication is
 * deliberate; keep the two specialized.
 */
function lowerBoundCrosshairX(points: CrosshairPoints[], x: number): number {
  let lo = 0;
  let hi = points.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (points[mid].point.x < x) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  return lo;
}

export function getCrosshairPoints(
  allCrosshairPoints: CrosshairPoints[],
  cursorX: number,
  xMin: number,
  xMax: number
): CrosshairPoints | null {
  if (allCrosshairPoints.length === 0) {
    return null;
  }

  // clamp to the x-domain (xMin <= xMax is an invariant; see pointIsReasonablyClose)
  const clampedHighlightX = Math.min(Math.max(cursorX, xMin), xMax);

  // inline binary search: leftmost index where point.x >= clampedHighlightX
  const pointIndex = lowerBoundCrosshairX(
    allCrosshairPoints,
    clampedHighlightX
  );

  // when the user's cursor is out past the last point in the line,
  // only return a point from this line if the end of line is reasonably close
  const lastPointX =
    allCrosshairPoints[allCrosshairPoints.length - 1]?.point?.x;

  if (pointIndex === allCrosshairPoints.length) {
    if (cursorX > lastPointX) {
      return pointIsReasonablyClose(cursorX, lastPointX, xMin, xMax)
        ? allCrosshairPoints[allCrosshairPoints.length - 1]
        : null;
    } else {
      return allCrosshairPoints[allCrosshairPoints.length - 1];
    }
  }

  // ditto but for the beginning of the line
  const firstPointX = allCrosshairPoints[0]?.point?.x;
  if (pointIndex === 0) {
    if (cursorX < firstPointX) {
      return pointIsReasonablyClose(cursorX, firstPointX, xMin, xMax)
        ? allCrosshairPoints[0]
        : null;
    } else {
      return allCrosshairPoints[0];
    }
  }

  // Show the point we're closest to
  const nextPoint = allCrosshairPoints[pointIndex];
  const prevPointIndex = Math.max(pointIndex - 1, 0);
  const prevPoint = allCrosshairPoints[prevPointIndex];

  const prevPointOutOfBounds = prevPoint.point.x < xMin;
  const nextPointOutOfBounds = nextPoint.point.x > xMax;
  const nextPointCloserThanPrevPoint =
    nextPoint.point.x - cursorX < cursorX - prevPoint.point.x;

  const useNextPoint =
    prevPointOutOfBounds ||
    (nextPointCloserThanPrevPoint && !nextPointOutOfBounds);

  return useNextPoint ? nextPoint : prevPoint;
}

export function mergeCrosshairPoints(
  nonNanPoints: CrosshairPoints[],
  nanPoints: CrosshairPoints[]
): CrosshairPoints[] {
  if (nanPoints.length === 0) {
    return nonNanPoints;
  }

  return nonNanPoints.concat(nanPoints).sort((a, b) => a.point.x - b.point.x);
}

export const pointIsReasonablyClose = (
  cursorX: number,
  pointX: number,
  xMin: number,
  xMax: number
): boolean => {
  const graphWidth = xMax - xMin;
  if (graphWidth <= 0) {
    captureError(
      'graphWidth was not a valid number!',
      'crosshairValuesFromLines',
      {extra: {xMin, xMax}}
    );
    // Not sure how we would hit this case, so I'm just allowing all points
    return true;
  }
  const distanceFromHighlight = Math.abs(pointX - cursorX);
  const percentOfGraphFromCursor = distanceFromHighlight / graphWidth;

  // don't show points from lines that are far away from the cursor
  // Note that this value is basically just a guess, so it would not
  // surprise me if it makes sense to tweak it a bit
  return percentOfGraphFromCursor < 0.05;
};

export interface MarkSeriesPointForCrosshair {
  color?: string;
  fill?: string | number;
  isBaseline?: boolean;
  isPinned?: boolean;
  mark?: Mark;
  metricName?: string;
  minmax?: Array<number | undefined>;
  name?: string;
  opacity?: string | number;
  original?: number;
  percent?: number;
  runFriendlyName: string;
  size?: string | number;
  stddev?: number;
  stroke?: string | number;
  title: string;
  total?: number;
  uniqueId?: string;
  value: string | number | Date;
  values?: number[] /* I don't know where this get filled in, might be unused? */;
  x: number;
  y: number;
  // prevX is used if the line plot setting `showXRange` is on in order to show the
  // x range of the bucket. The prevX (previous point's x value)
  // determines the end of the previous bucket and the start of the
  // current bucket.
  prevX?: number;
}

/**
 * Helper to create a MarkSeriesPointForCrosshair from line data and crosshair points.
 * Shared utility to avoid duplication between crosshairValuesFromLines and
 * baseline run fallback logic in LinePlotCrosshair.
 */
export function createCrosshairPoint(
  line: RenderLine,
  crosshairPoints: CrosshairPoints
): MarkSeriesPointForCrosshair {
  const {point, minmaxPoint, stddevPoint, prevPoint} = crosshairPoints;
  // TODO: Update full fidelity to use y0 as min instead of max
  const minPoint =
    line.provenance.mode === 'full-fidelity' ? minmaxPoint?.y : minmaxPoint?.y0;
  const maxPoint =
    line.provenance.mode === 'full-fidelity' ? minmaxPoint?.y0 : minmaxPoint?.y;

  return {
    color: line.color,
    isBaseline: line.isBaseline,
    isPinned: line.isPinned,
    mark: line.mark,
    metricName: line.metricName,
    minmax: minmaxPoint != null ? [minPoint, maxPoint] : undefined,
    name: line.runName,
    original: point.legendData?.original,
    percent: point.legendData?.percent,
    // line.displayName is populated when runs not grouped
    // line.highlightName retains the grouped display name.
    runFriendlyName: line.displayName || line.highlightName || '',
    stddev: stddevPoint != null ? stddevPoint.y - point.y : undefined,
    title: line.title ?? '',
    total: point.legendData?.total,
    uniqueId: line.highlightId ?? undefined,
    value: point.legendData?.y ?? point.y,
    x: point.x,
    y: point.y,
    prevX: prevPoint?.x,
  };
}

type LinesWithCrosshairPoints = Array<{
  line: RenderLine;
  crosshairPoints: CrosshairPoints[];
}>;

type CrosshairValuesCacheEntry = {
  cursorX: number;
  values: MarkSeriesPointForCrosshair[];
  xMax: number;
  xMin: number;
};

/**
 * Last result per line set, so a repeat call with the same cursor and x-domain
 * skips the work.
 *
 * A hovered panel asks for the same values twice on every mouse move: the
 * mousemove handler needs them to find the line nearest the cursor, and the
 * render that follows needs them again to draw the tooltip.
 *
 * The cache is keyed by the line set rather than being a single slot, because
 * every on-screen panel recomputes on the same cursor x. A sibling panel's call
 * lands between the hovered panel's two calls and would otherwise evict the
 * entry before it can be reused. Keys are held weakly, so an entry goes away
 * with the line set it belongs to.
 */
const crosshairValuesCache = new WeakMap<
  LinesWithCrosshairPoints,
  CrosshairValuesCacheEntry
>();

export function crosshairValuesFromLines(
  enabledLinesWithCrosshairPoints: LinesWithCrosshairPoints,
  cursorX: number,
  xMin: number,
  xMax: number
): MarkSeriesPointForCrosshair[] {
  const cached = crosshairValuesCache.get(enabledLinesWithCrosshairPoints);
  if (
    cached != null &&
    cached.cursorX === cursorX &&
    cached.xMin === xMin &&
    cached.xMax === xMax
  ) {
    return cached.values;
  }

  const values = computeCrosshairValuesFromLines(
    enabledLinesWithCrosshairPoints,
    cursorX,
    xMin,
    xMax
  );
  crosshairValuesCache.set(enabledLinesWithCrosshairPoints, {
    cursorX,
    values,
    xMax,
    xMin,
  });
  return values;
}

/**
 * The returned array is shared between callers (see the cache above), so treat
 * it as read-only.
 */
function computeCrosshairValuesFromLines(
  enabledLinesWithCrosshairPoints: LinesWithCrosshairPoints,
  cursorX: number,
  xMin: number,
  xMax: number
): MarkSeriesPointForCrosshair[] {
  const highlightPoints = enabledLinesWithCrosshairPoints
    .map(({line, crosshairPoints}) => {
      const crosshairPointsByCursor = getCrosshairPoints(
        crosshairPoints,
        cursorX,
        xMin,
        xMax
      );
      if (crosshairPointsByCursor == null) {
        return null;
      }
      return createCrosshairPoint(line, crosshairPointsByCursor);
    })
    .filter(notEmpty);

  // At this point, we have the closest X values (relative to cursorX) for each of the lines.
  // We now filter them to show only the lines with points that are close (relative to the
  // overall x range of the graph).
  //
  // Note that we do not filter to exact x value matches because:
  // 1. There are cases where there are no exact x value matches (eg from wandb backend sampling
  //    of long lines)
  // 2. The x value is usually steps, and depending on how the user is logging, there's no
  //    guarantee that steps will actually line up between runs.
  // So matching close points is actually better than exact x value matches.
  const xVals = new Set<number>();
  const dist = (xVal: number) => Math.abs(xVal - cursorX);

  highlightPoints.forEach(p => xVals.add(p.x));
  if (xVals.size > 1) {
    const closestX = Array.from(xVals).reduce(
      (acc: undefined | number, pointX: number) => {
        const accIsCloser = acc && dist(pointX) >= dist(acc);
        return accIsCloser ? acc : pointX;
      },
      undefined
    );
    if (closestX == null) {
      // xVals size is guaranteed to be > 1, so the reduce statement
      // shouldn't be returning undefined since it'll always return a value
      // if there's at least one point.
      console.warn('encountered null closestX');
      // safe default to return all points
      return highlightPoints;
    }

    return highlightPoints.filter(pt =>
      pointIsReasonablyClose(closestX, pt.x, xMin, xMax)
    );
  }
  return highlightPoints;
}
